import styled, { keyframes } from 'styled-components';

export const PanelRoot = styled.aside`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    background-color: #eef1f4;
    border-left: 1px solid #cad5e2;
    box-shadow: -8px 0 18px rgba(0, 0, 0, 0.12);
`;

export const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 68px;
    height: 68px;
    padding: 3px;
    box-sizing: border-box;
    background-color: #e5f3ff;
    border-bottom: 1px solid ${({ theme }) => theme.tokens?.BaseColorGray300};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    position: relative;
    z-index: 2;
`;

export const PanelLogo = styled.span`
    display: grid;
    place-items: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    border-radius: 7px;

    svg.deal-assistant-panel-logo {
        width: 20px;
        height: 20px;
        transform: scale(1.2);
    }
`;

export const PanelHeading = styled.div`
    flex: 1;
    min-width: 0;
`;

export const PanelTitle = styled.h2`
    margin: 0 0 2px;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.2;
    color: ${({ theme }) => theme.tokens?.BaseColorBlack};
`;

export const PanelSubtitle = styled.p`
    margin: 0;
    font-size: 12px;
    line-height: 1.15;
    color: ${({ theme }) => theme.tokens?.BaseColorGray600};
    overflow-wrap: anywhere;
`;

export const PanelCloseButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: ${({ theme }) => theme.tokens?.BaseColorBlack};
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.tokens?.BaseColorGray100};
    }

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const PanelThread = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 8px 8px 14px;
    overflow-y: auto;
    color: ${({ theme }) => theme.tokens?.BaseColorBlack};
`;

export const MessageRow = styled.div<{ $role?: string }>`
    display: flex;
    align-items: flex-start;
    gap: 7px;
    width: 100%;
    justify-content: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
`;

export const MessageLogo = styled.span`
    display: grid;
    place-items: center;
    flex: 0 0 24px;
    width: 24px;
    height: 24px;
    margin-top: 3px;

    svg.deal-assistant-message-logo {
        width: 20px;
        height: 20px;
        transform: scale(0.85);
    }
`;

export const WelcomeBubble = styled.div`
    flex: 1;
    min-width: 0;
    padding: 9px 11px;
    border: 1px solid #cad5e2;
    border-radius: 12px 12px 12px 3px;
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

export const WelcomeText = styled.p`
    margin: 0 0 8px;
    font-size: 13px;
    line-height: 1.35;
`;

export const WelcomeLinks = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
`;

export const WelcomeLink = styled.button`
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: ${({ theme }) => theme.tokens?.BaseColorBlue700};
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
`;

export const Message = styled.div<{ $role: string }>`
    min-width: 0;
    flex: ${({ $role }) => ($role === 'user' ? '0 1 auto' : '1')};
    max-width: ${({ $role }) => ($role === 'user' ? '84%' : '100%')};
    padding: 9px 11px;
    border: 1px solid ${({ $role, theme }) => ($role === 'user' ? theme.tokens?.BaseColorBlue700 : '#cad5e2')};
    border-radius: ${({ $role }) => ($role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px')};
    font-size: 13px;
    line-height: 1.35;
    white-space: pre-wrap;
    word-break: break-word;
    background-color: ${({ $role, theme }) => ($role === 'user' ? theme.tokens?.BaseColorBlue700 : theme.tokens?.BaseColorWhite)};
    color: ${({ $role, theme }) => ($role === 'user' ? theme.tokens?.BaseColorWhite : theme.tokens?.BaseColorBlack)};
`;

const typingDot = keyframes`
    0%,
    60%,
    100% {
        transform: translateY(0);
        opacity: 0.45;
    }
    30% {
        transform: translateY(-4px);
        opacity: 1;
    }
`;

export const TypingIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 54px;
    height: 34px;
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    border: 1px solid ${({ theme }) => theme.tokens?.BaseColorGray300};
    border-radius: 12px 12px 12px 3px;

    i {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: ${({ theme }) => theme.tokens?.BaseColorGray600};
        animation: ${typingDot} 1s infinite ease-in-out;
    }

    i:nth-child(2) {
        animation-delay: 0.15s;
    }

    i:nth-child(3) {
        animation-delay: 0.3s;
    }
`;

export const FailureMessage = styled.div`
    flex: 1;
    min-width: 0;
    padding: 8px 12px;
    border: 1px solid #b3261e;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    color: #b3261e;
    font-size: 14px;
    line-height: 1.3;
`;

export const PanelFooter = styled.div`
    padding: 2px 4px;
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    border-top: 1px solid ${({ theme }) => theme.tokens?.BaseColorGray300};
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
`;

export const PanelReason = styled.p`
    margin: 2px 4px;
    font-size: 12px;
    line-height: 1.3;
    color: ${({ theme }) => theme.tokens?.BaseColorGray600};
`;

export const Composer = styled.form`
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 42px;
    padding: 2px 3px 2px 8px;
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.tokens?.BaseColorGray300};
    border-radius: 12px;
    background-color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

    &:focus-within {
        border-color: ${({ theme }) => theme.tokens?.BaseColorBlue700};
        box-shadow: 0 0 0 1px ${({ theme }) => theme.tokens?.BaseColorBlue700};
    }

    svg.deal-assistant-composer-logo {
        flex: none;
        width: 20px;
        height: 20px;
    }
`;

export const ComposerInput = styled.textarea`
    flex: 1;
    min-width: 0;
    height: 30px;
    padding: 6px 0;
    box-sizing: border-box;
    border: 0;
    outline: 0;
    resize: none;
    background: transparent;
    font: inherit;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
    color: ${({ theme }) => theme.tokens?.BaseColorBlack};

    &::placeholder {
        color: ${({ theme }) => theme.tokens?.BaseColorGray600};
    }
`;

export const SendButton = styled.button`
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.tokens?.BaseColorBlue600};
    color: ${({ theme }) => theme.tokens?.BaseColorWhite};
    cursor: pointer;

    &:hover {
        background-color: ${({ theme }) => theme.tokens?.BaseColorBlue800};
    }

    &:disabled {
        background-color: ${({ theme }) => theme.tokens?.BaseColorGray300};
        cursor: not-allowed;
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;
